import { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';

interface LoadingSectionProps {
  title: string;
  /** Human-readable stage label (e.g. "Extracting pose data"). */
  stage?: string;
  /** Progress 0-100. Omit for an indeterminate bar. */
  pct?: number;
  /** When the job was submitted — drives the elapsed counter. */
  startedAt?: Date;
  /** Hint shown after 5 s of waiting (e.g. "Usually 20-40 seconds"). */
  expectedDurationLabel?: string;
  className?: string;
}

/**
 * LoadingSection — titled progress indicator with optional elapsed counter and
 * an expected-duration hint that fades in after 5 seconds.
 *
 * Stateless except for the elapsed counter (setInterval at 1 s). The component
 * does NOT re-render on every tick when `startedAt` is not supplied.
 */
function LoadingSection({
  title,
  stage,
  pct,
  startedAt,
  expectedDurationLabel,
  className,
}: LoadingSectionProps) {
  const [elapsedSec, setElapsedSec] = useState<number>(0);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    if (!startedAt) return;

    const tick = () => {
      const secs = Math.floor((Date.now() - startedAt.getTime()) / 1000);
      setElapsedSec(secs);
    };
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [startedAt]);

  // Fade-in the expected-duration hint after 5 s.
  useEffect(() => {
    const id = setTimeout(() => setShowHint(true), 5_000);
    return () => clearTimeout(id);
  }, []);

  const isIndeterminate = pct === undefined || pct === null;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-text-primary">{title}</span>
        {startedAt && (
          <span className="text-xs text-text-muted tabular-nums">
            {formatElapsed(elapsedSec)}
          </span>
        )}
      </div>

      {stage && (
        <p className="text-xs text-text-secondary">{stage}</p>
      )}

      {/* Progress bar */}
      <div
        className="relative h-1.5 w-full overflow-hidden rounded-full bg-dark-elevated-2"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={isIndeterminate ? undefined : pct}
        aria-valuetext={isIndeterminate ? 'Loading…' : `${pct}%`}
      >
        {isIndeterminate ? (
          // Indeterminate sliding bar
          <div className="absolute inset-y-0 w-1/3 animate-[slide_1.4s_ease-in-out_infinite] rounded-full bg-accent" />
        ) : (
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-300 ease-in-out"
            style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
          />
        )}
      </div>

      {expectedDurationLabel && (
        <p
          className={cn(
            'text-xs text-text-muted transition-opacity duration-500',
            showHint ? 'opacity-100' : 'opacity-0'
          )}
        >
          {expectedDurationLabel}
        </p>
      )}
    </div>
  );
}

function formatElapsed(totalSec: number): string {
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s}s`;
}

export default LoadingSection;
